// resources/js/app.js

import './bootstrap';
import Focus from '@alpinejs/focus';
import Collapse from '@alpinejs/collapse';
import MarkdownIt from 'markdown-it';
import hljs from 'highlight.js';
import DOMPurify from 'dompurify';

// ─── Alpine Setup (Livewire owns Alpine lifecycle) ───────────
// Livewire v3 boots Alpine automatically. Starting Alpine here
// creates duplicate instances and breaks event handling.
document.addEventListener('alpine:init', () => {
    if (window.Alpine) {
        window.Alpine.plugin(Focus);
        window.Alpine.plugin(Collapse);
        // Do not register @alpinejs/persist: Livewire 3’s Alpine build already defines $persist / persist.
    }
});

function escapeHtml(input) {
    return String(input ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

function codeWrapperHtml({ codeHtml, languageLabel, codeText }) {
    return `
        <div class="relative group/code my-3 nx-code-block">
            <div class="flex items-center justify-between px-4 py-2 bg-[#111113] border border-[#27272a] rounded-t-xl">
                <span class="text-xs text-zinc-500 font-mono">${escapeHtml(languageLabel)}</span>
                <button
                    type="button"
                    data-code-copy="${escapeHtml(codeText)}"
                    class="text-xs text-zinc-500 hover:text-white transition-colors px-2 py-0.5 rounded border border-transparent hover:border-zinc-600"
                >Copy</button>
            </div>
            <pre class="!rounded-t-none !mt-0 !border-t-0"><code class="hljs language-${escapeHtml(languageLabel)}">${codeHtml}</code></pre>
        </div>
    `;
}

const md = new MarkdownIt({
    html: false,
    linkify: true,
    breaks: true,
    typographer: true,
    highlight: (code, lang) => {
        const language = (lang || 'plaintext').trim();
        let highlighted = escapeHtml(code);
        try {
            if (language && hljs.getLanguage(language)) {
                highlighted = hljs.highlight(code, { language, ignoreIllegals: true }).value;
            } else {
                highlighted = hljs.highlightAuto(code).value;
            }
        } catch {}
        return codeWrapperHtml({
            codeHtml: highlighted,
            languageLabel: language || 'plaintext',
            codeText: code,
        });
    },
});

md.renderer.rules.table_open = () => '<div class="overflow-x-auto my-4 rounded-xl border border-[#27272a]"><table class="w-full text-sm">';
md.renderer.rules.table_close = () => '</table></div>';

window.hljs = hljs;
window.DOMPurify = DOMPurify;
window.markdownit = MarkdownIt;
window.nxMarkdown = md;
window.nxMarkdownParse = (source) => md.render(source ?? '');

window.decorateCodeBlocksHtml = function (html) {
    if (!html || typeof DOMParser === 'undefined') {
        return html;
    }
    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
    doc.querySelectorAll('pre').forEach((pre) => {
        if (pre.closest('.nx-code-block')) return;
        const code = pre.querySelector('code');
        if (!code) return;
        const className = code.className || '';
        const detectedLang = className.match(/language-([a-zA-Z0-9_+-]+)/)?.[1] || 'plaintext';
        const source = code.textContent || '';
        let highlighted = escapeHtml(source);
        try {
            if (hljs.getLanguage(detectedLang)) {
                highlighted = hljs.highlight(source, { language: detectedLang, ignoreIllegals: true }).value;
            } else {
                highlighted = hljs.highlightAuto(source).value;
            }
        } catch {}
        pre.outerHTML = codeWrapperHtml({
            codeHtml: highlighted,
            languageLabel: detectedLang,
            codeText: source,
        });
    });
    return doc.body.firstElementChild?.innerHTML ?? html;
};

function wireCodeCopyButtons(root = document) {
    root.querySelectorAll('[data-code-copy]').forEach((button) => {
        if (button.dataset.copyBound === '1') return;
        button.dataset.copyBound = '1';
        button.addEventListener('click', () => {
            const text = button.dataset.codeCopy || '';
            navigator.clipboard.writeText(text);
            const original = button.textContent;
            button.textContent = 'Copied!';
            setTimeout(() => {
                button.textContent = original || 'Copy';
            }, 1800);
        });
    });
}
window.wireCodeCopyButtons = wireCodeCopyButtons;

function decorateExistingMarkdownCode(root = document) {
    root.querySelectorAll('.nx-prose').forEach((container) => {
        if (!container.querySelector('pre')) return;
        container.innerHTML = window.decorateCodeBlocksHtml(container.innerHTML);
    });
    wireCodeCopyButtons(root);
}

// ─── Alpine Components ────────────────────────────────────────

// Global copy to clipboard
document.addEventListener('alpine:init', () => {
    if (!window.Alpine) return;

    window.Alpine.magic('copy', () => (text) => {
        navigator.clipboard.writeText(text);
    });

    // Chat auto-resize textarea
    window.Alpine.directive('autoresize', (el) => {
        const resize = () => {
            el.style.height = 'auto';
            el.style.height = Math.min(el.scrollHeight, 200) + 'px';
        };
        el.addEventListener('input', resize);
        resize();
    });
});

// ─── Re-highlight code blocks after Livewire DOM updates ──────
document.addEventListener('livewire:navigated', () => {
    decorateExistingMarkdownCode(document);
});

document.addEventListener('livewire:update', () => {
    setTimeout(() => {
        decorateExistingMarkdownCode(document);
    }, 100);
});

// ─── Theme initialization ─────────────────────────────────────
const savedTheme = localStorage.getItem('nx-dark');
const isDark = savedTheme !== 'false';
document.documentElement.classList.toggle('dark', isDark);
document.documentElement.classList.toggle('light', !isDark);
